import { useState } from "react";
import { motion } from "framer-motion";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Send, CheckCircle } from "lucide-react";

const ROOM_OPTIONS = [
  { value: "La Palomar ($75/noche - 2 personas)", label: "La Palomar — $75/noche · 2 personas" },
  { value: "Habitación Doble ($90/noche - 2-3 personas)", label: "Habitación Doble — $90/noche · 2–3 personas" },
  { value: "La Clúster ($120/noche - 4 personas, $155 para 6)", label: "La Clúster — $120/noche · 4 personas (máx. 6)" },
];

const REQUEST_TYPES = [
  { value: "Reservar habitación", label: "Reservar habitación" },
  { value: "Reservar evento", label: "Reservar evento" },
];

export const BookingForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());
    try {
      const formSubmitUrl = process.env.REACT_APP_FORMSUBMIT_URL;
      const response = await fetch(formSubmitUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ ...data, _subject: `Nueva solicitud: ${data.tipo_solicitud} — ${data.nombre}` }),
      });
      if (response.ok) { setSubmitted(true); toast.success("Solicitud enviada correctamente"); }
      else { toast.error("Error al enviar. Inténtalo de nuevo."); }
    } catch { toast.error("Error de conexión. Inténtalo más tarde."); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <section id="reservar" data-testid="booking-section" className="py-24 md:py-32 bg-[#EBE3D5]">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="max-w-xl mx-auto px-4 text-center">
          <CheckCircle className="mx-auto text-[#3F4F34] mb-6" size={56} />
          <h2 data-testid="booking-success-title" className="font-heading text-4xl text-[#2D2A26] tracking-tight">Solicitud Enviada</h2>
          <p className="mt-4 text-[#5C5750] font-body text-base leading-relaxed">Hemos recibido tu solicitud. Nos pondremos en contacto contigo lo antes posible para confirmar los detalles.</p>
          <button data-testid="booking-new-request" onClick={() => setSubmitted(false)} className="mt-8 overline text-[0.7rem] border border-[#3F4F34] text-[#3F4F34] px-8 py-3 hover:bg-[#3F4F34] hover:text-[#FAF7F2] transition-all duration-300">Nueva solicitud</button>
        </motion.div>
      </section>
    );
  }

  return (
    <section id="reservar" data-testid="booking-section" className="py-24 md:py-32 bg-[#EBE3D5]">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 md:px-12">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.8 }} className="text-center mb-14">
          <p className="overline text-[#A25F4B] mb-4">Reservaciones</p>
          <h2 data-testid="booking-title" className="font-heading text-4xl md:text-5xl tracking-tight text-[#2D2A26]">Reserva tu Experiencia</h2>
          <p className="mt-4 text-[#5C5750] font-body text-base max-w-lg mx-auto leading-relaxed">
            Reserva una habitación o un evento privado en Suchimex. Completa el formulario y te contactaremos para confirmar.
          </p>
        </motion.div>

        <motion.form onSubmit={handleSubmit} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }} data-testid="booking-form" className="space-y-8 bg-[#FAF7F2] p-8 md:p-12 border border-[#D4C3A3]/40">
          <input type="hidden" name="_captcha" value="false" />
          <input type="hidden" name="_template" value="table" />

          {/* Tipo de solicitud */}
          <div className="space-y-2">
            <Label className="overline text-[0.65rem] text-[#5C5750]">Tipo de solicitud *</Label>
            <div className="flex gap-3">
              {REQUEST_TYPES.map((type) => (
                <label key={type.value} className="flex-1">
                  <input type="radio" name="tipo_solicitud" value={type.value} required className="peer sr-only" />
                  <div data-testid={`request-type-${type.value.includes("habitación") ? "room" : "event"}`} className="text-center p-3 border border-[#D4C3A3] cursor-pointer font-body text-sm text-[#5C5750] transition-all peer-checked:border-[#3F4F34] peer-checked:bg-[#3F4F34]/5 peer-checked:text-[#3F4F34] peer-checked:font-semibold hover:border-[#A25F4B]">
                    {type.label}
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Selección de habitación */}
          <div className="space-y-2">
            <Label className="overline text-[0.65rem] text-[#5C5750]">Habitación preferida (si aplica)</Label>
            <select name="habitacion" data-testid="booking-select-room" className="w-full border-0 border-b border-[#D4C3A3] bg-transparent py-2 px-0 text-[#2D2A26] font-body text-sm focus:outline-none focus:border-[#3F4F34]">
              <option value="">— Selecciona una opción —</option>
              {ROOM_OPTIONS.map((room) => (
                <option key={room.value} value={room.value}>{room.label}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="nombre" className="overline text-[0.65rem] text-[#5C5750]">Nombre completo *</Label>
              <Input id="nombre" name="nombre" required data-testid="booking-input-nombre" placeholder="Tu nombre" className="border-0 border-b border-[#D4C3A3] rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-[#3F4F34] shadow-none px-0 text-[#2D2A26] font-body" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="telefono" className="overline text-[0.65rem] text-[#5C5750]">Teléfono / WhatsApp *</Label>
              <Input id="telefono" name="telefono" type="tel" required data-testid="booking-input-telefono" placeholder="+503 0000-0000" className="border-0 border-b border-[#D4C3A3] rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-[#3F4F34] shadow-none px-0 text-[#2D2A26] font-body" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="overline text-[0.65rem] text-[#5C5750]">Correo electrónico *</Label>
            <Input id="email" name="email" type="email" required data-testid="booking-input-email" placeholder="tu@correo.com" className="border-0 border-b border-[#D4C3A3] rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-[#3F4F34] shadow-none px-0 text-[#2D2A26] font-body" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-2">
              <Label htmlFor="fecha_entrada" className="overline text-[0.65rem] text-[#5C5750]">Fecha de entrada</Label>
              <Input id="fecha_entrada" name="fecha_entrada" type="date" data-testid="booking-input-fecha-entrada" className="border-0 border-b border-[#D4C3A3] rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-[#3F4F34] shadow-none px-0 text-[#2D2A26] font-body" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="fecha_salida" className="overline text-[0.65rem] text-[#5C5750]">Fecha de salida</Label>
              <Input id="fecha_salida" name="fecha_salida" type="date" data-testid="booking-input-fecha-salida" className="border-0 border-b border-[#D4C3A3] rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-[#3F4F34] shadow-none px-0 text-[#2D2A26] font-body" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="personas" className="overline text-[0.65rem] text-[#5C5750]">Personas</Label>
              <Input id="personas" name="personas" type="number" min="1" max="50" data-testid="booking-input-personas" placeholder="2" className="border-0 border-b border-[#D4C3A3] rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-[#3F4F34] shadow-none px-0 text-[#2D2A26] font-body" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="mensaje" className="overline text-[0.65rem] text-[#5C5750]">Mensaje o solicitud especial</Label>
            <Textarea id="mensaje" name="mensaje" rows={4} data-testid="booking-input-mensaje" placeholder="Cuéntanos los detalles de tu evento o solicitud especial..." className="border-0 border-b border-[#D4C3A3] rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-[#3F4F34] shadow-none px-0 text-[#2D2A26] font-body resize-none" />
          </div>

          <div className="pt-4">
            <button type="submit" disabled={loading} data-testid="booking-submit-button" className="overline text-[0.7rem] bg-[#3F4F34] text-[#FAF7F2] px-10 py-3.5 hover:bg-[#2B3822] transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2">
              {loading ? "Enviando..." : <><Send size={14} /> Enviar Solicitud</>}
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};
